import { useState, useMemo, useEffect } from 'react';
import {Platform, View, Image, Text, FlatList } from 'react-native';
import React from 'react';
import styles from '@stylesheet';
import apis from '@api/api';
import SearchListUserItem from './components/SearchListUserItem';
import useGlobalUser from '@hooks/getGlobalUser';
import { DropDownPicker } from '@constants/Themed';
import UserListItem from '@navigation/screens/MidLevel/UsersScreen/Components/UserListItem';
import { useQueryClient } from '@tanstack/react-query';
import { UserFinder as UserFinderType } from './types/FinderTypes';
import { MaterialIcons } from '@expo/vector-icons';
import { Text as TamaguiText } from 'tamagui';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';


const UserFinder = ({ hideList, team, onOpen, onClose, forceSingle, setCallback, disabled, selected, removePermission, forceRow = false, theme = true, hasError = false, maxDropdownHeight = 350 }: UserFinderType) => {
  const [value, setValue] = useState<any>(selected);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const userId = useGlobalUser();
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [height, setHeight] = useState(50);

  // Sync value state with selected prop when it changes externally
  useEffect(() => {
    if (selected !== undefined && selected !== value) {
      setValue(selected);
    }
  }, [selected]);

  const queryClient = useQueryClient();
  const companyListData = team ? queryClient.getQueryData(['companyList']) : null;
  const companyMembersData = team ? queryClient.getQueryData(['companyMembers']) : null;
  // Fallback to userHierarchy query which should have populated users
  const userHierarchyData = team ? queryClient.getQueryData(['userHierarchy']) : null;

  // Transform team members to DropDownPicker format (label, value, etc.)
  const teamMembers = useMemo(() => {
    if (!team) {
      return [];
    }

    let rawUsers: any[] = [];

    // Handle companyMembers (user hierarchy data) - could be array or wrapped in data property
    if (companyMembersData) {
      if (Array.isArray(companyMembersData)) {
        rawUsers = companyMembersData;
      } else if (typeof companyMembersData === 'object' && companyMembersData !== null && 'data' in companyMembersData) {
        const data = (companyMembersData as any).data;
        if (Array.isArray(data)) {
          rawUsers = data;
        }
      }
    }

    // Fallback: Try userHierarchy query which should have populated users
    if (rawUsers.length === 0 && userHierarchyData) {
      if (typeof userHierarchyData === 'object' && userHierarchyData !== null && 'data' in userHierarchyData) {
        const data = (userHierarchyData as any).data;
        if (Array.isArray(data)) {
          rawUsers = data;
        }
      } else if (Array.isArray(userHierarchyData)) {
        rawUsers = userHierarchyData;
      }
    }
    // Handle companyList (companies with users arrays) - extract users from companies
    if (rawUsers.length === 0 && companyListData) {
      let companies: any[] = [];
      if (Array.isArray(companyListData)) {
        companies = companyListData;
      } else if (typeof companyListData === 'object' && companyListData !== null && 'data' in companyListData) {
        const data = (companyListData as any).data;
        if (Array.isArray(data)) {
          companies = data;
        }
      }

      // Extract users from all companies' users arrays
      companies.forEach((company: any) => {
        if (company.users && Array.isArray(company.users)) {
          rawUsers.push(...company.users);
        }
      });
    }

    if (!rawUsers || rawUsers.length === 0) {
      console.log('UserFinder: No raw users found', { companyMembersData, companyListData });
      return [];
    }

    const transformed = rawUsers
      .filter((member: any) => {
        // Check if member.user is a populated object (not a string ID)
        // Backend should populate users, but handle both cases
        if (member.user && typeof member.user === 'object' && member.user._id) {
          // Populated user object - valid
          return true;
        }

        // If member.user is a string ID, we can't use it without fetching user data
        // Skip these entries as we don't have the user details
        if (member.user && typeof member.user === 'string') {
          console.log('UserFinder: Skipping member with unpopulated user ID', {
            userId: member.user,
            memberId: member._id
          });
          return false;
        }

        // If member itself is a user object (direct user, not nested)
        if (member._id && (member.email || member.profile_information)) {
          return true;
        }

        // Invalid entry
        console.log('UserFinder: Filtered out invalid member', {
          hasUser: !!member.user,
          userType: typeof member.user,
          userIsObject: member.user && typeof member.user === 'object',
          userHasId: member.user?._id,
          memberKeys: Object.keys(member || {})
        });
        return false;
      })
      .map((member: any) => {
        // Handle populated nested structure (member.user is an object)
        if (member.user && typeof member.user === 'object' && member.user._id) {
          const user = member.user;
          const userId = user._id || user.id;
          const name = user.profile_information?.name || user.name || '';
          const surname = user.profile_information?.surname || user.surname || '';
          const email = user.email || '';
          const avatar = user.profile_information?.avatar?.img || user.avatar?.img || user.avatar || '';

          // Format label like searchUsers: "Name Surname\nemail"
          const fullName = `${name} ${surname}`.trim() || email || 'Unknown User';
          const label = email ? `${fullName}\n${email}` : fullName;

          return {
            label,
            value: userId,
            name,
            surname,
            email,
            avatar,
            _id: userId,
            // Preserve original member data for reference
            originalMember: member
          };
        }

        // Handle direct user object (member itself is the user)
        if (member._id && (member.email || member.profile_information)) {
          const userId = member._id || member.id;
          const name = member.profile_information?.name || member.name || '';
          const surname = member.profile_information?.surname || member.surname || '';
          const email = member.email || '';
          const avatar = member.profile_information?.avatar?.img || member.avatar?.img || member.avatar || '';

          // Format label like searchUsers: "Name Surname\nemail"
          const fullName = `${name} ${surname}`.trim() || email || 'Unknown User';
          const label = email ? `${fullName}\n${email}` : fullName;

          return {
            label,
            value: userId,
            name,
            surname,
            email,
            avatar,
            _id: userId
          };
        }

        // Fallback (shouldn't reach here due to filter)
        console.warn('UserFinder: Unexpected member structure in map', member);
        return null;
      })
      .filter((item: any) => {
        // Remove null entries and current user
        if (!item) return false;
        const isNotCurrentUser = item.value !== userId;
        if (!isNotCurrentUser) {
          console.log('UserFinder: Filtered out current user', item);
        }
        return isNotCurrentUser;
      });

    console.log('UserFinder: Transformed team members', {
      rawCount: rawUsers.length,
      transformedCount: transformed.length,
      sample: transformed[0],
      hasCompanyMembers: !!companyMembersData,
      hasUserHierarchy: !!userHierarchyData,
      hasCompanyList: !!companyListData
    });

    return transformed;
  }, [team, companyListData, companyMembersData, userHierarchyData, userId]);

  // Get the currently selected user for display
  const currentSelectedUser = useMemo(() => {
    if (!selectedUsers || selectedUsers.length === 0) return null;
    return forceSingle ? selectedUsers[0] : selectedUsers[0]; // For display, show first selected
  }, [selectedUsers, forceSingle]);

  // Sync selectedUsers with value from DropDownPicker
  // This ensures selectedUsers stays in sync when value changes (e.g., from external updates or initial load)
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setSelectedUsers([]);
      return;
    }

    const allItems = team ? teamMembers : items;
    if (!allItems || allItems.length === 0) {
      // If items aren't loaded yet, wait for them to load
      return;
    }

    let usersToSet: any[] = [];

    if (forceSingle) {
      // Single selection mode
      const selectedItem = allItems.find((item: any) => item.value === value);
      if (selectedItem) {
        usersToSet = [{
          ...selectedItem,
          editPermission: false,
          id: selectedItem.value || selectedItem._id
        }];
      }
    } else {
      // Multiple selection mode
      if (Array.isArray(value)) {
        const valueArray = value as any[];
        const selectedItems = allItems.filter((item: any) => valueArray.includes(item.value));
        usersToSet = selectedItems.map((item: any) => ({
          ...item,
          editPermission: false,
          id: item.value || item._id
        }));
      } else if (value) {
        // Handle case where value is a single value but not in array format
        const selectedItem = allItems.find((item: any) => item.value === value);
        if (selectedItem) {
          usersToSet = [{
            ...selectedItem,
            editPermission: false,
            id: selectedItem.value || selectedItem._id
          }];
        }
      }
    }

    setSelectedUsers(usersToSet);
  }, [value, team, teamMembers, items, forceSingle]);

  const handleItemSelect = (selectedItem: any) => {
    // DropDownPicker's onSelectItem is called when user selects/deselects an item
    // In multiple mode, it receives an array of ALL currently selected items
    // In single mode, it receives the selected item object
    // We transform the data and update both selectedUsers and call the callback
    if (forceSingle) {
      // Single selection: selectedItem is a single object
      const transformedItem = {
        ...selectedItem,
        editPermission: false,
        id: selectedItem.value || selectedItem._id
      };
      setSelectedUsers([transformedItem]);
      // Update value state to keep it in sync
      setValue(selectedItem.value);
      setCallback?.(transformedItem);
    } else {
      // Multiple selection: selectedItem is an array of all selected items
      const itemsArray = Array.isArray(selectedItem) ? selectedItem : [selectedItem];
      const transformedItems = itemsArray.map((item: any) => ({
        ...item,
        editPermission: false,
        id: item.value || item._id
      }));
      setSelectedUsers(transformedItems);
      // Update value state to keep it in sync with all selected values
      const selectedValues = itemsArray.map((item: any) => item.value);
      setValue(selectedValues);
      setCallback?.(transformedItems);
    }
  };

  return (
    <View style={{ flex: 1, width: '100%', backgroundColor: 'transparent' }}>
      {/* Heading Section */}
      <View style={{ marginBottom: 20 }}>
        <TamaguiText style={{
          fontSize: 20,
          fontWeight: '700',
          color: theme ? '#1e293b' : '#f8fafc',
          marginBottom: 16
        }}>
          Select User for Context Analysis
        </TamaguiText>

        {/* User Label */}
        <TamaguiText style={{
          fontSize: 14,
          fontWeight: '600',
          color: theme ? '#475569' : '#94a3b8',
          marginBottom: 8
        }}>
          User
        </TamaguiText>
      </View>

      {/* Dropdown Section */}
      <View style={{
        zIndex: 999999,
        minHeight: 50,
        borderRadius: 12,
        backgroundColor: 'transparent',
        position: 'relative',
        marginBottom: 20
      }}>
        <DropDownPicker
          zIndex={999999}
          zIndexInverse={999998}
          setOpen={() => {
            setOpen(true);
            setHeight(300);
            onOpen && onOpen?.();
          }}
          open={open}
          key={selected}
          multiple={forceSingle ? false : true}
          loading={loading}
          onOpen={async () => {
            setLoading(true);
            onOpen && onOpen?.();
            // Only fetch users if not using team mode
            if (!team) {
              await apis.searchUsers("")
                .then((items: any) => {
                  const filteredItems = items.filter(item => item.value !== userId);
                  setItems(filteredItems)
                })
                .catch(err => { console.error(err) })
                .finally(() => {
                  setLoading(false)
                })
            } else {
              // In team mode, data comes from teamMembers (already transformed)
              setLoading(false);
            }
          }}
          style={[
            styles.dropdownInput,
            {
              paddingHorizontal: 16,
              paddingVertical: 14,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: hasError ? '#ef4444' : (open ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              backgroundColor: theme ? 'white' : '#1e293b',
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 999999,
              zIndex: 999999,
              position: 'relative'
            }
          ]}
          textStyle={{
            color: value && (Array.isArray(value) ? value.length > 0 : value)
              ? (theme ? '#1e293b' : '#f8fafc')
              : (theme ? '#64748b' : '#94a3b8'),
            fontSize: 16,
            fontWeight: value && (Array.isArray(value) ? value.length > 0 : value) ? '600' : '400'
          }}
          placeholderStyle={{
            color: theme ? '#64748b' : '#94a3b8',
            fontSize: 16
          }}
          mode='SIMPLE'
          items={team ? teamMembers : items}
          onClose={() => {
            setHeight(50);
            setOpen(false);
            onClose && onClose?.()
          }}
          onChangeSearchText={async (text) => {
            setLoading(true);
            await apis.searchUsers(text)
              .then((items: any) => {
                setItems(items)
              })
              .catch(err => { console.error(err) })
              .finally(() => {
                setLoading(false)
              })
          }}
          setItems={setItems}
          debounce
          disabled={disabled}
          value={value}
          setValue={setValue}
          showTickIcon
          badgeDotColors={["#e76f51", "#00b4d8", "#e9c46a", "#e76f51", "#8ac926", "#00b4d8", "#e9c46a"]}
          selectedItemContainerStyle={{
            backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
            borderRadius: 8,
            marginVertical: 2
          }}
          listItemContainerStyle={{
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#f1f5f9' : '#334155'
          }}
          listItemLabelStyle={{
            fontSize: 12,
            color: theme ? '#1e293b' : '#f8fafc',
            fontWeight: '500'
          }}
          closeAfterSelecting={false}
          dropDownContainerStyle={{
            top: '100%',
            marginTop: -15,
            borderRadius: 12,
            borderColor: theme ? '#e2e8f0' : '#374151',
            borderWidth: 1,
            zIndex: 999999,
            elevation: 999999,
            backgroundColor: theme ? 'white' : '#1e293b',
            position: 'absolute',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            maxHeight: maxDropdownHeight
          }}
          searchContainerStyle={{
            padding: 12,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#f1f5f9' : '#334155'
          }}
          searchTextInputStyle={{
            backgroundColor: theme ? '#f8fafc' : '#0f172a',
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#475569',
            borderRadius: 8,
            paddingHorizontal: 12,
            paddingVertical: 8,
            fontSize: 14,
            color: theme ? '#1e293b' : '#f8fafc'
          }}
          placeholder='Search users by email or name...'
          searchable={true}
          modal={true}
          modalAnimationType="slide"
          modalTitle="Select User"
          renderListItem={(props) => (<SearchListUserItem {...props} />)}
          addCustomItem={true}
          onSelectItem={(item) => { handleItemSelect(item) }}
          searchPlaceholder='Enter an email address'
        />
      </View>

      {/* User Display Area */}
      <View style={{
        backgroundColor: theme ? 'white' : '#1e293b',
        borderRadius: 16,
        padding: 24,
        minHeight: 200,
        borderWidth: 2,
        borderColor: theme ? '#e2e8f0' : '#334155',
        borderStyle: 'dashed',
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
          backdropFilter: 'blur(10px)',
        })
      }}>
        {!currentSelectedUser ? (
          // Empty state
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingVertical: 40
          }}>
            <MaterialIcons
              name="person-outline"
              size={64}
              color={theme ? '#cbd5e1' : '#475569'}
              style={{ marginBottom: 16 }}
            />
            <TamaguiText style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              No User Selected
            </TamaguiText>
            <TamaguiText style={{
              color: theme ? '#94a3b8' : '#64748b',
              fontSize: 14,
              textAlign: 'center'
            }}>
              Select a user from the dropdown above
            </TamaguiText>
          </View>
        ) : (
          // Selected user display
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingVertical: 20
          }}>
            {/* Avatar */}
            <View style={{
              marginBottom: 16,
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AvatarHandler
                avatar={currentSelectedUser.avatar || ''}
                avatarImageStyle={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  borderWidth: 3,
                  borderColor: theme ? '#e2e8f0' : '#334155'
                }}
                replacementImageStyle={{
                  width: 80,
                  height: 80,
                  borderRadius: 40
                }}
              />
            </View>

            {/* Name */}
            <TamaguiText style={{
              color: theme ? '#1e293b' : '#f8fafc',
              fontSize: 20,
              fontWeight: '700',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              {`${currentSelectedUser.name || ''} ${currentSelectedUser.surname || ''}`.trim() || 'Unknown User'}
            </TamaguiText>

            {/* Email */}
            <TamaguiText style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 16,
              fontWeight: '400',
              textAlign: 'center',
              flexWrap: 'wrap',
              maxWidth: '90%'
            }}>
              {currentSelectedUser.email || ''}
            </TamaguiText>
          </View>
        )}

        {/* Multiple users list (if not forceSingle and multiple selected) */}
        {!hideList && !forceSingle && selectedUsers.length > 1 && (
          <View style={{
            width: '100%',
            marginTop: 24,
            paddingTop: 24,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e2e8f0' : '#334155'
          }}>
            <TamaguiText style={{
              color: theme ? '#475569' : '#94a3b8',
              fontSize: 14,
              fontWeight: '600',
              marginBottom: 12
            }}>
              Additional Selected Users ({selectedUsers.length - 1})
            </TamaguiText>
            <FlatList
              style={{
                maxHeight: 200
              }}
              showsVerticalScrollIndicator={true}
              data={selectedUsers.slice(1)}
              horizontal={forceRow}
              contentContainerStyle={{
                gap: 8,
                paddingVertical: 10
              }}
              renderItem={({ item, index }) => (
                <UserListItem
                  removePermission={removePermission}
                  row={forceRow}
                  item={item}
                  index={index + 1}
                  filterUser={(id: string) => {
                    const updatedUsers = selectedUsers.filter((user) => user.id !== id);
                    setSelectedUsers(updatedUsers);
                    if (forceSingle) {
                      setValue(null);
                    } else {
                      const updatedValues = updatedUsers.map((user) => user.id || user.value || user._id);
                      setValue(updatedValues);
                    }
                    setCallback?.(updatedUsers);
                  }}
                />
              )}
            />
          </View>
        )}
      </View>
    </View>
  )
}
export default UserFinder;
